import { useEffect, useCallback, useRef } from "react";
import axios from "axios";

const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
const ACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes

export function useUserActivity() {
  const lastActivityTimeRef = useRef<number>(Date.now());
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isImpersonationRef = useRef<boolean>(false);

  // Check if we're in an impersonation session
  useEffect(() => {
    const checkImpersonation = () => {
      const impersonationInfo = localStorage.getItem("impersonation_info");
      isImpersonationRef.current = !!impersonationInfo;
    };

    checkImpersonation();
  }, []);

  // Track user activity
  const trackActivity = useCallback(async (type: "heartbeat" | "visit" | "focus" = "heartbeat") => {
    if (isImpersonationRef.current) {
      console.log("Skipping activity tracking for impersonation session");
      return;
    }

    try {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityTimeRef.current;

      // Update if it's a visit, focus, or enough time has passed for heartbeat
      if (type === "visit" || type === "focus" || timeSinceLastActivity >= ACTIVITY_THRESHOLD) {
        await axios.post("/user-activity/track", { type });
        lastActivityTimeRef.current = now;
      }
    } catch (error) {
      console.error("Failed to track user activity:", error);
    }
  }, []);

  // Setup heartbeat
  useEffect(() => {
    if (isImpersonationRef.current) {
      return;
    }

    // Initial visit tracking
    trackActivity("visit");

    // Start heartbeat interval
    heartbeatIntervalRef.current = setInterval(() => {
      trackActivity("heartbeat");
    }, HEARTBEAT_INTERVAL);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [trackActivity]);

  // Setup visibility and focus listeners
  useEffect(() => {
    if (isImpersonationRef.current) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        trackActivity("focus");
      }
    };

    const handleWindowFocus = () => {
      trackActivity("focus");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [trackActivity]);

  return {
    trackActivity,
  };
}
